import { useState } from "react";
import { data, redirect, Form } from "react-router";
import type { Route } from "./+types/brands.pricing";
import { Button } from "~/components/ui/button";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { getActorFromRequest } from "~/lib/session.server";
import { detectLocale } from "~/i18n/index";
import { translations } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";
import type { BuyerPlanTier, BillingInterval } from "@caratzon/domain";

type TierDef = {
  key: BuyerPlanTier;
  name: string;
  tagline: string;
  description: string;
  monthlyPrice: number;
  annualPrice: number;
  studioCredits: number;
  welcomeCredits?: number;
  features: string[];
  highlight?: boolean;
  contactSales?: boolean;
};

export async function loader({ request }: Route.LoaderArgs) {
  const actor = await getActorFromRequest(request);
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  const url = new URL(request.url);
  const rawReason = url.searchParams.get("reason");
  const reason = rawReason === "search" || rawReason === "ai_search" ? rawReason : null;
  return { actor, locale, reason };
}

export async function action({ request }: Route.ActionArgs) {
  const actor = await getActorFromRequest(request);
  if (!actor.authenticated) throw redirect("/sign-in");

  const formData = await request.formData();
  const intent = String(formData.get("intent") ?? "");
  if (intent !== "subscribe") {
    return data({ ok: false, error: "Unknown action" }, { status: 400 });
  }

  const plan = String(formData.get("plan")) as BuyerPlanTier;
  const billingInterval = String(formData.get("billingInterval")) as BillingInterval;

  if (!["professional", "atelier", "enterprise"].includes(plan)) {
    return data({ ok: false, error: "Invalid plan" }, { status: 400 });
  }
  if (!["monthly", "annual"].includes(billingInterval)) {
    return data({ ok: false, error: "Invalid billing interval" }, { status: 400 });
  }

  const { isStripeLive } = await import("@caratzon/integrations");

  if (!isStripeLive()) {
    const { createBuyerSubscription } = await import("@caratzon/server");
    await createBuyerSubscription({ userId: actor.id, plan, billingInterval });
    return redirect("/trade/catalog");
  }

  const { createBuyerCatalogCheckout, createStripeCustomer } = await import("@caratzon/integrations");
  const { getBuyerSubscription } = await import("@caratzon/server");
  const origin = new URL(request.url).origin;

  const existingSub = await getBuyerSubscription(actor.id);
  let stripeCustomerId = existingSub?.stripeCustomerId;
  if (!stripeCustomerId) {
    const customer = await createStripeCustomer({
      email: `${actor.id}@caratzon.com`,
      name: actor.name ?? actor.id,
      userId: actor.id,
    });
    stripeCustomerId = customer.customerId;
  }

  const session = await createBuyerCatalogCheckout({
    plan: plan as "professional" | "atelier",
    billingInterval,
    stripeCustomerId,
    userId: actor.id,
    successUrl: `${origin}/trade/catalog?subscription=success`,
    cancelUrl: `${origin}/brands/pricing`,
  });

  return redirect(session.checkoutUrl);
}

export function meta({ data: loaderData }: Route.MetaArgs) {
  const locale = (loaderData?.locale ?? "en") as Locale;
  const t = translations[locale];
  const title = t.pricing?.meta?.title ?? "Pricing · Caratzon";
  const description = t.pricing?.meta?.description ?? "Transparent pricing for brands on Caratzon — source pre-engineered jewelry designs, generate AI marketing kits, and manufacture sustainably.";
  return [
    { title },
    { name: "description", content: description },
    ...socialMeta({ title, description, pathname: "/brands/pricing", locale }),
  ];
}

export default function PricingRoute({ loaderData, actionData }: Route.ComponentProps) {
  const { actor, reason } = loaderData;
  const { t } = useT();
  const p = t.pricing;
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const TIERS: TierDef[] = [
    {
      key: "explorer",
      name: p.tiers.explorer.name,
      tagline: p.tiers.explorer.tagline,
      description: p.tiers.explorer.description,
      monthlyPrice: 0,
      annualPrice: 0,
      studioCredits: 0,
      welcomeCredits: 200,
      features: p.tiers.explorer.features,
    },
    {
      key: "professional",
      name: p.tiers.professional.name,
      tagline: p.tiers.professional.tagline,
      description: p.tiers.professional.description,
      monthlyPrice: 149,
      annualPrice: 1490,
      studioCredits: 1_500,
      highlight: true,
      features: p.tiers.professional.features,
    },
    {
      key: "atelier",
      name: p.tiers.atelier.name,
      tagline: p.tiers.atelier.tagline,
      description: p.tiers.atelier.description,
      monthlyPrice: 399,
      annualPrice: 3990,
      studioCredits: 5_000,
      features: p.tiers.atelier.features,
    },
    {
      key: "enterprise",
      name: p.tiers.enterprise.name,
      tagline: p.tiers.enterprise.tagline,
      description: p.tiers.enterprise.description,
      monthlyPrice: 0,
      annualPrice: 0,
      studioCredits: 0,
      contactSales: true,
      features: p.tiers.enterprise.features,
    },
  ];

  return (
    <MarketingLayout>
    <div className="px-6 lg:px-10 py-12 lg:py-16">

      {/* ── Hero ── */}
      <div className="max-w-3xl mx-auto text-center mb-14">
        <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>
          {p.hero.overline}
        </span>
        <h1
          className="font-display font-light leading-[1.06] tracking-tight text-foreground mt-4"
          style={{ fontSize: "clamp(2.6rem, 5vw, 4.2rem)" }}
        >
          {p.hero.heading}
          <br />
          <em className="not-italic" style={{ opacity: 0.4 }}>{p.hero.headingEm}</em>
        </h1>
        <p
          className="text-muted-foreground mt-5 leading-relaxed mx-auto"
          style={{ fontSize: "0.95rem", maxWidth: "56ch" }}
        >
          {p.hero.body}
        </p>
      </div>

      {/* ── Social Proof ── */}
      <div className="max-w-2xl mx-auto mb-14">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {p.socialProof.map((stat) => (
            <span
              key={stat}
              className="flex items-center gap-2 text-muted-foreground"
              style={{ fontSize: "0.78rem" }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full shrink-0"
                style={{ background: "var(--teal-sapphire)", opacity: 0.6 }}
              />
              {stat}
            </span>
          ))}
        </div>
      </div>

      {/* ── Upgrade prompt banner (shown when redirected from catalog) ── */}
      {reason && (
        <div
          className="max-w-2xl mx-auto mb-10 rounded-xl border px-6 py-4 flex items-start gap-4"
          style={{ background: "oklch(0.45 0.08 213 / 0.07)", borderColor: "oklch(0.45 0.08 213 / 0.25)" }}
        >
          <svg
            className="shrink-0 mt-0.5"
            style={{ color: "var(--teal-sapphire)" }}
            width="18"
            height="18"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <div>
            <p className="font-medium text-foreground" style={{ fontSize: "0.88rem" }}>
              {reason === "ai_search"
                ? "AI visual search requires an Atelier plan or above."
                : "Catalogue search and filters require a Professional plan or above."}
            </p>
            <p className="text-muted-foreground mt-1" style={{ fontSize: "0.80rem" }}>
              {reason === "ai_search"
                ? "Your current Explorer plan includes browsing featured designs. Upgrade to unlock AI-powered visual search, full catalogue access, and sourcing tools."
                : "Your current Explorer plan includes browsing featured designs. Upgrade to unlock search, advanced filters, and the full design catalogue."}
            </p>
          </div>
        </div>
      )}

      {/* ── Billing Toggle ── */}
      <div className="flex items-center justify-center gap-1 mb-14">
        <div className="inline-flex rounded-full border border-border p-1 bg-muted/50">
          <button
            type="button"
            onClick={() => setBillingInterval("monthly")}
            className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
              billingInterval === "monthly"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.billing.monthly}
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval("annual")}
            className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
              billingInterval === "annual"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.billing.annual}
          </button>
        </div>
        <span
          className="ml-3 font-medium"
          style={{ fontSize: "0.75rem", letterSpacing: "0.04em", color: "var(--teal-sapphire)" }}
        >
          {billingInterval === "annual" ? p.billing.savingActive : p.billing.savingPrompt}
        </span>
      </div>

      {/* ── Subscription error banner ── */}
      {actionData && "error" in actionData && actionData.error ? (
        <div className="max-w-2xl mx-auto mb-8 rounded-xl border border-destructive/30 bg-destructive/8 px-5 py-3.5">
          <p className="text-sm text-destructive">{actionData.error}</p>
        </div>
      ) : null}

      {/* ── Tier Cards ── */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto items-start">
        {TIERS.map((tier, tierIndex) => {
          const price = billingInterval === "monthly" ? tier.monthlyPrice : tier.annualPrice;

          return (
            <div
              key={tier.key}
              className={`rounded-2xl border bg-card flex flex-col transition-shadow duration-300 ${
                tier.highlight
                  ? "border-border ring-1 shadow-[0_2px_24px_rgba(0,0,0,0.06)] relative"
                  : "border-border hover:shadow-[0_2px_16px_rgba(0,0,0,0.04)]"
              }`}
              style={{
                animationDelay: `${tierIndex * 80}ms`,
                ...(tier.highlight ? { borderColor: "oklch(0.45 0.08 213 / 0.3)", ringColor: "oklch(0.45 0.08 213 / 0.15)" } as React.CSSProperties : {}),
              }}
            >
              {tier.highlight ? (
                <div className="flex justify-center -mt-3">
                  <span
                    className="inline-block px-4 py-1 rounded-full text-white font-medium"
                    style={{
                      fontSize: "0.65rem",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      background: "var(--teal-sapphire)",
                    }}
                  >
                    {p.tiers.professional.mostPopular}
                  </span>
                </div>
              ) : null}

              <div className={`p-8 lg:p-10 flex flex-col flex-1 ${tier.highlight ? "pt-5" : ""}`}>
                {/* Tier header */}
                <div className="mb-6">
                  <span
                    className="issue-label block mb-2"
                    style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}
                  >
                    {tier.tagline}
                  </span>
                  <h2
                    className="font-display font-light text-foreground mb-1"
                    style={{ fontSize: "1.8rem", lineHeight: 1.1 }}
                  >
                    {tier.name}
                  </h2>
                  <p
                    className="text-muted-foreground leading-relaxed mt-2"
                    style={{ fontSize: "0.82rem", minHeight: "3.2em" }}
                  >
                    {tier.description}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {tier.contactSales ? (
                    <div>
                      <span
                        className="font-display font-light text-foreground"
                        style={{ fontSize: "2.4rem", lineHeight: 1 }}
                      >
                        {p.tiers.enterprise.customLabel}
                      </span>
                      <p className="text-muted-foreground mt-1.5" style={{ fontSize: "0.7rem" }}>
                        {p.tiers.enterprise.customSublabel}
                      </p>
                    </div>
                  ) : price === 0 ? (
                    <div>
                      <span
                        className="font-display font-light text-foreground"
                        style={{ fontSize: "2.8rem", lineHeight: 1 }}
                      >
                        {p.priceDisplay.free}
                      </span>
                      <p className="text-muted-foreground mt-1.5" style={{ fontSize: "0.7rem" }}>
                        {p.priceDisplay.freeSublabel}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span
                          className="font-display font-light text-foreground"
                          style={{ fontSize: "2.8rem", lineHeight: 1 }}
                        >
                          ${billingInterval === "monthly" ? price : Math.round(price / 12)}
                        </span>
                        <span className="text-muted-foreground" style={{ fontSize: "0.85rem" }}>
                          {p.priceDisplay.perMonth}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-1.5" style={{ fontSize: "0.75rem" }}>
                        {billingInterval === "monthly" ? (
                          <>
                            {p.priceDisplay.orAnnualOption.replace("{amount}", "$" + tier.annualPrice.toLocaleString())}{" "}
                            <span style={{ color: "var(--teal-sapphire)" }} className="font-medium">{p.priceDisplay.saveBadge}</span>
                          </>
                        ) : (
                          <>{p.priceDisplay.billedAnnually.replace("{amount}", "$" + tier.annualPrice.toLocaleString())}</>
                        )}
                      </p>
                      <p className="text-muted-foreground mt-1" style={{ fontSize: "0.7rem" }}>
                        {p.priceDisplay.licensesSeparate}
                      </p>
                    </>
                  )}
                </div>

                {/* CTA */}
                <div className="mb-8">
                  {tier.contactSales ? (
                    <Button asChild variant="outline" className="w-full py-5">
                      <a href="/contact">{p.tiers[tier.key].cta}</a>
                    </Button>
                  ) : tier.key === "explorer" ? (
                    <Button asChild variant="outline" className="w-full py-5">
                      <a href={actor.authenticated ? "/trade/catalog" : "/sign-in"}>
                        {p.tiers.explorer.cta}
                      </a>
                    </Button>
                  ) : actor.authenticated ? (
                    <Form method="post">
                      <input type="hidden" name="intent" value="subscribe" />
                      <input type="hidden" name="plan" value={tier.key} />
                      <input type="hidden" name="billingInterval" value={billingInterval} />
                      <Button type="submit" className="w-full py-5">
                        {p.tiers[tier.key].cta}
                      </Button>
                    </Form>
                  ) : (
                    <Button asChild className="w-full py-5">
                      <a href="/sign-in">{p.getStarted}</a>
                    </Button>
                  )}
                </div>

                <div className="rule mb-6" />

                {/* Feature list */}
                <ul className="space-y-3.5 flex-1">
                  {tier.features.map((feature, fi) => {
                    const isGroupHeader = feature.endsWith(":");
                    return (
                      <li
                        key={fi}
                        className="flex items-start gap-3"
                        style={{ fontSize: "0.84rem" }}
                      >
                        {isGroupHeader ? (
                          <span className="text-muted-foreground/60 font-medium">{feature}</span>
                        ) : (
                          <>
                            <svg
                              className="shrink-0 mt-0.5"
                              style={{ color: "var(--teal-sapphire)" }}
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="text-foreground/80">{feature}</span>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Studio Credits cross-link ── */}
      <div className="max-w-4xl mx-auto mt-20 mb-8">
        <div className="rule mb-10" />
        <div className="text-center">
          <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>
            {p.studioCredits.overline}
          </span>
          <h2
            className="font-display font-light text-foreground mt-3"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.4rem)" }}
          >
            {p.studioCredits.heading}
          </h2>
          <p
            className="text-muted-foreground mt-4 leading-relaxed mx-auto"
            style={{ fontSize: "0.95rem", maxWidth: "52ch" }}
          >
            {p.studioCredits.body}
          </p>
          <a
            href="/ai/credits"
            className="inline-block mt-6 transition-colors"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "0.75rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--teal-sapphire)",
              borderBottom: "1px solid var(--teal-sapphire)",
              paddingBottom: "0.15rem",
            }}
          >
            {p.studioCredits.link}
          </a>
        </div>
      </div>

      {/* ── ROI Comparison ── */}
      <div className="max-w-4xl mx-auto mt-20 mb-8">
        <div className="rule mb-10" />
        <div className="text-center mb-10">
          <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>
            {p.roi.overline}
          </span>
          <h2
            className="font-display font-light text-foreground mt-3"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}
          >
            {p.roi.heading}
          </h2>
          <p
            className="text-muted-foreground mt-4 leading-relaxed mx-auto"
            style={{ fontSize: "0.95rem", maxWidth: "52ch" }}
          >
            {p.roi.body}
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-3 border-b border-border">
            <div className="p-5 lg:p-6" />
            <div className="p-5 lg:p-6 border-l border-border">
              <span
                className="text-muted-foreground/60 block font-medium"
                style={{ fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                {p.roi.traditionalLabel}
              </span>
            </div>
            <div
              className="p-5 lg:p-6 border-l"
              style={{ borderColor: "var(--teal-sapphire)", background: "oklch(0.45 0.08 213 / 0.04)" }}
            >
              <span
                className="block font-medium"
                style={{ fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--teal-sapphire)" }}
              >
                {p.roi.caratzonLabel}
              </span>
            </div>
          </div>

          {p.roi.rows.map((row, i, arr) => (
            <div
              key={row.metric}
              className={`grid grid-cols-3 ${i < arr.length - 1 ? "border-b border-border" : ""}`}
            >
              <div className="p-5 lg:p-6 flex items-center">
                <span className="text-foreground/80 font-medium" style={{ fontSize: "0.84rem" }}>
                  {row.metric}
                </span>
              </div>
              <div className="p-5 lg:p-6 border-l border-border flex items-center">
                <span
                  className="text-muted-foreground/60"
                  style={{ fontSize: "0.84rem", textDecoration: "line-through", textDecorationColor: "var(--muted-foreground)" }}
                >
                  {row.traditional}
                </span>
              </div>
              <div
                className="p-5 lg:p-6 border-l flex items-center"
                style={{ borderColor: "var(--teal-sapphire)", background: "oklch(0.45 0.08 213 / 0.04)" }}
              >
                <span
                  className="font-medium"
                  style={{ fontSize: "0.84rem", color: "var(--teal-sapphire)" }}
                >
                  {row.caratzon}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="max-w-3xl mx-auto mt-20 mb-8">
        <div className="rule mb-10" />
        <div className="text-center mb-10">
          <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>
            {p.faq.overline}
          </span>
          <h2
            className="font-display font-light text-foreground mt-3"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.4rem)" }}
          >
            {p.faq.heading}
          </h2>
        </div>

        <div className="space-y-0">
          {p.faq.items.map((item, i) => (
            <div key={i}>
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-start justify-between gap-4 py-5 text-left transition-colors hover:text-foreground"
                style={{ fontSize: "0.9rem" }}
              >
                <span className={`font-medium leading-snug ${openFaq === i ? "text-foreground" : "text-foreground/80"}`}>
                  {item.q}
                </span>
                <svg
                  className="shrink-0 mt-1 text-muted-foreground transition-transform duration-200"
                  style={{ transform: openFaq === i ? "rotate(180deg)" : undefined }}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                className="overflow-hidden transition-all duration-200 ease-out"
                style={{
                  maxHeight: openFaq === i ? "300px" : "0px",
                  opacity: openFaq === i ? 1 : 0,
                }}
              >
                <p
                  className="text-muted-foreground leading-relaxed pb-5"
                  style={{ fontSize: "0.84rem", maxWidth: "56ch" }}
                >
                  {item.a}
                </p>
              </div>
              <div className="rule" />
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom CTA ── */}
      <div className="text-center mt-16 mb-8 max-w-xl mx-auto">
        <h3
          className="font-display font-light text-foreground mb-3"
          style={{ fontSize: "1.6rem" }}
        >
          {p.bottomCta.heading}
        </h3>
        <p className="text-muted-foreground leading-relaxed mb-6" style={{ fontSize: "0.85rem" }}>
          {p.bottomCta.body}
        </p>
        <div className="flex items-center justify-center gap-4">
          <Button asChild className="bg-teal text-teal-fg hover:bg-teal-hover px-8 py-5">
            <a href={actor.authenticated ? "/trade/catalog" : "/sign-in?redirect=/trade/catalog"}>
              {p.bottomCta.button}
            </a>
          </Button>
          <a
            href="/contact"
            className="text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontSize: "0.82rem", textDecoration: "underline", textUnderlineOffset: "3px" }}
          >
            {p.bottomCta.link}
          </a>
        </div>
      </div>

    </div>
    </MarketingLayout>
  );
}
